import { BriefcaseBusiness, Files, FileText } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetBillingUsage } from "~/api/billing/billing";
import { useListProjects } from "~/api/folders/folders";
import { Loader } from "~/components/Loader";
import { Card, CardContent } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import {
  getStoredLocale,
  processUsageData,
  useOrganizationParams,
} from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { MonthSelector } from "./usage/MonthSelector";
import { SettingsChart } from "./usage/SettingsChart";

export const SettingsUsage: FC = () => {
  useTrackPageVisit("app/settings_usage/visit");
  const { organizationId } = useOrganizationParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [projectOptions, setProjectOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedCase, setSelectedCase] = useState<string>("all");

  const { from, to } = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    const lastDay = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

    return {
      from: firstDay.toISOString(),
      to: lastDay.toISOString(),
    };
  }, [selectedDate]);

  const intl = useIntl();
  const storedLocale = getStoredLocale() || "en";

  const { data, isLoading } = useGetBillingUsage(organizationId, { from, to });
  const { data: casesData, isLoading: isLoadingCases } =
    useListProjects(organizationId);

  const chartData = useMemo(() => {
    if (!data) return [];
    const filteredUsage =
      selectedCase === "all"
        ? data.usage
        : data.usage.filter((item) => item.folderId === selectedCase);
    return processUsageData({ ...data, usage: filteredUsage }, from);
  }, [data, selectedCase, from]);

  const pagesUsed = useMemo(() => {
    return data
      ? data.usage.reduce(
          (prevVal, currentVal) => prevVal + (currentVal.quantity ?? 0),
          0
        )
      : 0;
  }, [data]);

  const handleGetMonth = (from: string) => {
    setSelectedDate(new Date(from));
  };

  useEffect(() => {
    if (casesData) {
      const options = casesData.projects.map((project) => ({
        value: project.id,
        label: project.name,
      }));
      setProjectOptions([
        {
          value: "all",
          label: intl.formatMessage({
            id: "settings.usage.allCases",
            defaultMessage: "All Cases",
          }),
        },
        ...options,
      ]);
    }
  }, [casesData]);

  if (isLoadingCases)
    return (
      <div className="flex justify-center items-center w-[924px]">
        <Loader />
      </div>
    );

  return (
    <div className="flex flex-col gap-6 mt-2">
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-semibold">
          <FormattedMessage id="settings.usage" defaultMessage={"Usage"} />
        </h3>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.usage.hint"
            defaultMessage={
              "Statistics on the number of processed pages in all your cases and documents"
            }
          />
        </span>
      </div>
      <div className="flex gap-4 items-center">
        <Select onValueChange={(value) => setSelectedCase(value)}>
          <SelectTrigger className="w-[240px] !h-10">
            <SelectValue
              placeholder={intl.formatMessage({
                id: "settings.usage.allCases",
                defaultMessage: "All Cases",
              })}
            />
          </SelectTrigger>
          <SelectContent className="max-w-[240px]">
            <SelectGroup>
              {projectOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Separator orientation="vertical" className="h-6" />
        <div className="w-[191px]">
          <MonthSelector onMonthChange={handleGetMonth} />
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center w-[924px]">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex gap-6">
            <Card className="w-[292px]">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.pagesUsed"
                        defaultMessage={"Pages Processed"}
                      />
                    </span>
                    <Files className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {pagesUsed.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="w-[292px]">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.casesCreated"
                        defaultMessage={"Cases Created"}
                      />
                    </span>
                    <BriefcaseBusiness className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {data?.counts.cases?.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="w-[292px]">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground font-semibold">
                      <FormattedMessage
                        id="settings.usage.filesUploaded"
                        defaultMessage={"Documents Uploaded"}
                      />
                    </span>
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-card-foregroud text-2xl font-semibold tracking-tight">
                    {data?.counts.files?.toLocaleString(storedLocale)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
          <SettingsChart data={chartData} />
        </div>
      )}
      <div className="flex gap-4 justify-between"></div>
    </div>
  );
};
